import React from "react";
import { useTranslation } from "react-i18next";
import {
    Filter,
    Grid,
    useActionReportLogic,
    useContentDialog,
    useFormContext,
    useFormDialog,
} from "@programari-limit/base-react";
import {
    Alert,
    Box,
    Icon,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import GridFilterField from "components/GridFilterField";
import { SpringFilterQueryBuilder } from "utils/spring-filter-query-builder";
import { likeAny } from "utils/springFilterUtils";
import MuiGrid from "@mui/material/Grid";
import { useNavigate } from "react-router";
import RegistresVerifactuContent from "./RegistresVerifactuDetall";
import GridFormField from "components/GridFormField";

const RegistresVerifactuEsmenarContent = () => {
    const { data } = useFormContext();
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography>
                    {/* TODO: Traduir */}
                    Numero de factura:&nbsp;
                    <Typography component={"span"} sx={{ fontWeight: "bold" }}>
                        {data.numSerieFactura}
                    </Typography>
                </Typography>
                {/* TODO: Traduir */}
                <Typography>Error que subsanar:</Typography>
                <Alert severity="warning">
                    {data.respostaDescripcionError}
                </Alert>
                <Alert severity="info">
                    {/* TODO: Traduir */}
                    Rellene los campos necesarios para subsanar el error y pulse
                    ejecutar para remitir de nuevo la factura a verifactu.
                </Alert>
            </Box>
            <MuiGrid container spacing={2}>
                <GridFormField name="fechaExpedicionFactura" lg={4} />
                <GridFormField name="tipusImpostAplicacio" lg={4} />
                <GridFormField name="tipusClauRegim" lg={4} />
            </MuiGrid>
        </Box>
    );
};

// Filtres de la taula
const FilterContent = (props) => {
    const { filterApiRef } = props;
    const { t } = useTranslation();

    return (
        <MuiGrid container spacing={2}>
            <GridFilterField name="numSerieFactura" lg={2} />
            <GridFilterField name="fechaIniciExpedicionFactura" lg={2} />
            <GridFilterField name="fechaFiExpedicionFactura" lg={2} />
            <GridFilterField name="sifNumeroInstalacion" lg={2} />
            <GridFilterField name="respostaEstadoRegistro" lg={2} />
            {/* <GridFilterField
                name="sinRegistrar"
                lg={1.5}
                type="checkbox-select"
            /> */}

            <MuiGrid item lg={0.5}>
                <Tooltip title={t("comu.resetFiltre")}>
                    <IconButton
                        onClick={() => filterApiRef?.current?.doReset()}
                    >
                        <Icon>filter_alt_off</Icon>
                    </IconButton>
                </Tooltip>
            </MuiGrid>
        </MuiGrid>
    );
};

const springFilterBuilder = (data) => {
    const { and, equal, like, ge, le, in: inn } = SpringFilterQueryBuilder;

    const filter = and(
        like("numSerieFactura", likeAny(data?.numSerieFactura)),
        ge("fechaExpedicionFactura", data?.fechaIniciExpedicionFactura),
        le("fechaExpedicionFactura", data?.fechaFiExpedicionFactura),
        like("sifNumeroInstalacion", likeAny(data?.sifNumeroInstalacion)),
        data?.respostaEstadoRegistro?.length
            ? inn("respostaEstadoRegistro", ...data.respostaEstadoRegistro)
            : undefined,
        // equal("sinRegistrar", data?.sinRegistrar?.toString()),
    ).toString();

    return filter;
};

const RegistresVerifactuFilter = (props) => {
    const { onSpringFilterChange, onFilterInitialized } = props;
    const filterApiRef = React.useRef();

    return (
        <Box sx={{ mb: 2 }}>
            <Filter
                name={"RVF_FILTER"}
                resourceName={"registreVerifactu"}
                persistentState
                springFilterBuilder={springFilterBuilder}
                onSpringFilterChange={onSpringFilterChange}
                onFilterInitialized={onFilterInitialized}
                size="small"
                apiRef={filterApiRef}
                initialOnChangeRequest
            >
                <FilterContent filterApiRef={filterApiRef} />
            </Filter>
        </Box>
    );
};

const RegistresVerifactu = () => {
    const { t } = useTranslation();
    const [fixedFilter, setFixedFilter] = React.useState();
    const [filterInitialized, setFilterInitialized] = React.useState(false);
    const gridApiRef = React.useRef();
    const navigate = useNavigate();
    const columns = [
        {
            name: "numSerieFactura",
            flex: 0.5,
        },
        {
            name: "fechaExpedicionFactura",
            flex: 0.5,
        },
        {
            name: "importeTotal",
            flex: 0.3,
        },
        {
            name: "enviamentDataEnviament",
            flex: 0.5,
            valueFormatter: ({ value }) => {
                if (!value) return "";

                const date = new Date(value);

                const pad = (n) => String(n).padStart(2, "0");

                return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
            },
        },
        {
            name: "respostaEstadoRegistro",
            flex: 0.5,
        },
        {
            name: "respostaDescripcionError",
            flex: 2.5,
        },
    ];

    const { dialog: dialogDetall, openDialog } = useContentDialog("lg");

    const goDetallDialog = (row) => {
        openDialog(
            t("registreVerifactu.title"), // TODO: Posa traducció
            "search",
            "inherit",
            <RegistresVerifactuContent data={row} />,
            [
                {
                    value: "close",
                    contained: true,
                    startIcon: "close",
                    description: t("comu.tancar"),
                },
            ],
            (value, close) => {
                close();
            },
        );
    };

    const { dialog: actionRenviarFacturaDialog, openActionDialog: openActionRenviarFacturaDialog } =
        useFormDialog('registreVerifactu');
    const {
        execActionReport: execRenviarFacturaActionReport,
        loading: loadingRenvioFactura,
        buttonDisabled: buttonRenviarFacturaDisabled,
    } = useActionReportLogic(null, 'registreVerifactu', {
        action: 'RVF_ESMENAR',
        detached: true,
        formChildren: <RegistresVerifactuEsmenarContent />,
        onSuccess: gridApiRef?.current?.doRefresh,
        openActionFormDialog: openActionRenviarFacturaDialog,
        title: t('registreVerifactu.correccioErrorsRemissioTitle') // TODO: Traduir
    });

    const executeRenviarFacturaAction = (row) => {
        if (loadingRenvioFactura) {
            console.warn('Verifactu resend action not executed due to loading state');
            return;
        }
		
        const staticData = {
            registreVerifactuId: row?.id,
            numSerieFactura: row?.numSerieFactura,
            respostaDescripcionError: row?.respostaDescripcionError,
            fechaExpedicionFactura: row?.fechaExpedicionFactura,
            tipusImpostAplicacio: row?.desgloses[0].impuesto,
            tipusClauRegim: row?.desgloses[0].claveRegimen,
            respostaEstadoRegistro: row?.respostaEstadoRegistro,
            sifNumeroInstalacion: row?.sifNumeroInstalacion
        };		
        execRenviarFacturaActionReport(null, false, false, null, staticData);
    };

    const rowAdditionalActions = (row) => {
        const listActions = [];
        const tipusFactura = row?.tipusFactura;

        listActions.push({
            icon: "launch",
            label: t("registreVerifactu.factura"),
            showInMenu: true,
            onClick: () => {
                if (tipusFactura === "RESERVA") {
                    navigate(
                        `/reserva/form/${row?.idFactura}?backUrl=/registreVerifactu`,
                    );
                }
                if (tipusFactura === "OPERACIO") {
                    navigate(
                        `/operacio/form/${row?.idFactura}?backUrl=/registreVerifactu`,
                    );
                }
            },
        });
        listActions.push({
            icon: "search",
            label: t("comu.detall"),
            showInMenu: false,
            onClick: () => {
                goDetallDialog(row);
            },
        });

        listActions.push({
            icon: 'calculate',
            label: "Reenviar factura a Verifactu", // TODO: Traduir
            onClick: (params) => {
                executeRenviarFacturaAction(row);                
            },
            disabled: buttonRenviarFacturaDisabled,
            showInMenu: true,
        });

        return listActions;
    };

    return (
        <>
            <Grid
                apiRef={gridApiRef}
                resourceName="registreVerifactu"
                title={t("registresVerifactu.title")}
                columns={columns}
                fixedFilter={fixedFilter}
                findDisabled={!filterInitialized}
                rowAdditionalActions={(params) =>
                    rowAdditionalActions(params.row)
                }
                toolbarAdditionalRow={
                    <RegistresVerifactuFilter
                        onSpringFilterChange={setFixedFilter}
                        onFilterInitialized={() => setFilterInitialized(true)}
                    />
                }
                readOnly
                formRoutePath="form"
                density="compact"
                defaultSortModel={[
                    {
                        field: "numSerieFactura",
                        sort: "asc",
                    },
                ]}
            />
            {dialogDetall}
            {actionRenviarFacturaDialog}
        </>
    );
};
export default RegistresVerifactu;
